import { v4 as uuidv4 } from 'uuid';

import type { CashFlowTransaction } from 'app-components/flux/transactions/transactionsTypes';

type WorkflowState = {
  open: boolean;
  showUnsavedSplitsChanges: boolean;
  showUnsavedRecurringPrompt: boolean;
  screen: WorkflowScreen;
  txnClientId: string | null;
  waitingForTxn: boolean;
  waitingForTransfer: boolean;
  completionText: string | null;
  modelIdToLink: string | null | undefined;
  otherTransfer: CashFlowTransaction | null;
};

type WorkflowAction =
  | { type: 'closeDialog' }
  | { type: 'exitUnsavedDialog' }
  | { type: 'showUnsavedSplitsDialog' }
  | { type: 'showUnsavedRecurringDialog' }
  | { type: 'showRecurringOptions' }
  | { type: 'stopRecurringOptions' }
  | { type: 'startRecurringSelect' }
  | { type: 'exitRecurringSelect' }
  | { type: 'startEditRecurring' }
  | { type: 'exitEditRecurring' }
  | { type: 'resetToEdit' }
  | { type: 'startWaitingForTxn'; clientId: string }
  | { type: 'stopWaitingForTxn' }
  | { type: 'clearTxnClientId' }
  | { type: 'startWaitingForTransfer' }
  | { type: 'showOtherTransfer'; txn: CashFlowTransaction }
  | { type: 'setModelIdToLink'; modelId: string | null | undefined }
  | { type: 'clearModelIdToLink' }
  | { type: 'showCompletion'; text: string }
  | { type: 'saveRecurringComplete' }
  | { type: 'updateRecurringComplete' }
  | { type: 'closeEditSeries' }
  | { type: 'closeReview' }
  | { type: 'showConfirmation' }
  | { type: 'startEditSeries'; initialTab?: number; assignSplits?: boolean };

/** The top-level recurring workflow surface. Inline transaction content is not included here. */
export type WorkflowView =
  | 'transaction'
  | 'recurring-options'
  | 'recurring-create'
  | 'recurring-select'
  | 'review'
  | 'edit-series'
  | 'progress';

export type WorkflowScreen =
  | { type: 'transaction' }
  | { type: 'recurring-options' }
  | { type: 'recurring-select' }
  | { type: 'recurring-create' }
  | { type: 'review' }
  | { type: 'edit-series'; initialTab: number; assignSplits: boolean };

export const workflowActions = {
  clearModelIdToLink: (): WorkflowAction => ({ type: 'clearModelIdToLink' }),
  clearTxnClientId: (): WorkflowAction => ({ type: 'clearTxnClientId' }),
  closeDialog: (): WorkflowAction => ({ type: 'closeDialog' }),
  closeEditSeries: (): WorkflowAction => ({ type: 'closeEditSeries' }),
  closeReview: (): WorkflowAction => ({ type: 'closeReview' }),
  exitEditRecurring: (): WorkflowAction => ({ type: 'exitEditRecurring' }),
  exitRecurringSelect: (): WorkflowAction => ({ type: 'exitRecurringSelect' }),
  exitUnsavedDialog: (): WorkflowAction => ({ type: 'exitUnsavedDialog' }),
  resetToEdit: (): WorkflowAction => ({ type: 'resetToEdit' }),
  saveRecurringComplete: (): WorkflowAction => ({ type: 'saveRecurringComplete' }),
  setModelIdToLink: (modelId: string | null | undefined): WorkflowAction => ({
    type: 'setModelIdToLink',
    modelId,
  }),
  showCompletion: (text: string): WorkflowAction => ({ type: 'showCompletion', text }),
  showOtherTransfer: (txn: CashFlowTransaction): WorkflowAction => ({ type: 'showOtherTransfer', txn }),
  showRecurringOptions: (): WorkflowAction => ({ type: 'showRecurringOptions' }),
  showUnsavedRecurringDialog: (): WorkflowAction => ({ type: 'showUnsavedRecurringDialog' }),
  showUnsavedSplitsDialog: (): WorkflowAction => ({ type: 'showUnsavedSplitsDialog' }),
  startEditRecurring: (): WorkflowAction => ({ type: 'startEditRecurring' }),
  startEditSeries: (initialTab?: number, assignSplits?: boolean): WorkflowAction => ({
    type: 'startEditSeries',
    initialTab,
    assignSplits,
  }),
  startRecurringSelect: (): WorkflowAction => ({ type: 'startRecurringSelect' }),
  startWaitingForTransfer: (): WorkflowAction => ({ type: 'startWaitingForTransfer' }),
  startWaitingForTxn: (clientId: string): WorkflowAction => ({
    type: 'startWaitingForTxn',
    clientId,
  }),
  stopRecurringOptions: (): WorkflowAction => ({ type: 'stopRecurringOptions' }),
  stopWaitingForTxn: (): WorkflowAction => ({ type: 'stopWaitingForTxn' }),
  updateRecurringComplete: (): WorkflowAction => ({ type: 'updateRecurringComplete' }),
};

export function createWorkflowState(recurring: boolean | string | undefined): WorkflowState {
  return {
    open: true,
    showUnsavedSplitsChanges: false,
    showUnsavedRecurringPrompt: false,
    screen: recurring ? { type: 'recurring-options' } : { type: 'transaction' },
    txnClientId: uuidv4().toUpperCase(),
    waitingForTxn: false,
    waitingForTransfer: false,
    completionText: null,
    modelIdToLink: null,
    otherTransfer: null,
  };
}

export function workflowReducer(state: WorkflowState, action: WorkflowAction): WorkflowState {
  switch (action.type) {
    case 'closeDialog':
      return { ...state, open: false };
    case 'exitUnsavedDialog':
      return { ...state, showUnsavedSplitsChanges: false, showUnsavedRecurringPrompt: false };
    case 'showUnsavedSplitsDialog':
      return { ...state, showUnsavedSplitsChanges: true, showUnsavedRecurringPrompt: false };
    case 'showUnsavedRecurringDialog':
      return { ...state, showUnsavedSplitsChanges: true, showUnsavedRecurringPrompt: true };
    case 'showRecurringOptions':
      return { ...state, screen: { type: 'recurring-options' } };
    case 'stopRecurringOptions':
      return { ...state, screen: { type: 'transaction' } };
    case 'startRecurringSelect':
      return { ...state, screen: { type: 'recurring-select' } };
    case 'exitRecurringSelect':
      return { ...state, screen: { type: 'recurring-options' } };
    case 'startEditRecurring':
      return { ...state, screen: { type: 'recurring-create' } };
    case 'exitEditRecurring':
      return { ...state, screen: { type: 'recurring-options' } };
    case 'resetToEdit':
      return { ...state, screen: { type: 'transaction' } };
    case 'startWaitingForTxn':
      return { ...state, waitingForTxn: true, txnClientId: action.clientId };
    case 'stopWaitingForTxn':
      return { ...state, waitingForTxn: false };
    case 'clearTxnClientId':
      return { ...state, txnClientId: null };
    case 'startWaitingForTransfer':
      return { ...state, waitingForTransfer: true };
    case 'showOtherTransfer':
      return { ...state, otherTransfer: action.txn, waitingForTransfer: false };
    case 'setModelIdToLink':
      return { ...state, modelIdToLink: action.modelId };
    case 'clearModelIdToLink':
      return { ...state, modelIdToLink: null };
    case 'showCompletion':
      return { ...state, completionText: action.text };
    case 'saveRecurringComplete':
      return {
        ...state,
        screen: { type: 'review' },
      };
    case 'updateRecurringComplete':
      return {
        ...state,
        screen: { type: 'review' },
      };
    case 'closeEditSeries':
      return {
        ...state,
        screen: { type: 'transaction' },
      };
    case 'closeReview':
      return {
        ...state,
        screen: { type: 'transaction' },
      };
    case 'showConfirmation':
      return { ...state, screen: { type: 'review' } };
    case 'startEditSeries':
      return {
        ...state,
        screen: {
          type: 'edit-series',
          initialTab: action.initialTab ?? 0,
          assignSplits: action.assignSplits ?? false,
        },
      };
    default:
      return state;
  }
}

export function getWorkflowView(
  state: WorkflowState,
  { model, create }: { model: unknown; create: boolean },
): {
  dialogOpen: boolean;
  loadingLine: string;
  view: WorkflowView;
  showEditRecurring: boolean;
  showRecurringOptions: boolean;
  showRecurringSelect: boolean;
  waiting: boolean;
} {
  const showEditRecurring = state.screen.type === 'recurring-create';
  const showRecurringSelect = state.screen.type === 'recurring-select';
  const showRecurringOptions = state.screen.type === 'recurring-options' || showEditRecurring || showRecurringSelect;
  const waiting = state.waitingForTxn || state.waitingForTransfer || (showEditRecurring && !model);
  const loadingLine = state.waitingForTransfer
    ? 'Finding transfer'
    : state.modelIdToLink || (!create && state.waitingForTxn)
      ? 'Assigning to recurring series'
      : 'Creating Series';
  const view: WorkflowView =
    waiting || state.completionText
      ? 'progress'
      : state.screen.type === 'review'
        ? 'review'
        : state.screen.type === 'edit-series'
          ? 'edit-series'
          : state.screen.type === 'recurring-create'
            ? 'recurring-create'
            : state.screen.type === 'recurring-select'
              ? 'recurring-select'
              : state.screen.type === 'recurring-options'
                ? 'recurring-options'
                : 'transaction';
  const dialogOpen =
    state.open &&
    state.screen.type !== 'edit-series' &&
    !showEditRecurring &&
    !showRecurringSelect &&
    !waiting &&
    !state.completionText &&
    state.screen.type !== 'review';

  return { dialogOpen, loadingLine, showEditRecurring, showRecurringOptions, showRecurringSelect, view, waiting };
}

export function getTitle(
  state: WorkflowState,
  { create, isIncome, isTransfer }: { create: boolean; isIncome: boolean; isTransfer: boolean },
): string {
  if (state.screen.type === 'transaction') return create ? 'Create transaction' : 'Transaction detail';
  if (isTransfer) return 'Mark as recurring transfer';
  return isIncome ? 'Mark as recurring income' : 'Mark as bill or recurring';
}

export type { WorkflowAction, WorkflowState };
